// Video source selection for ExperienceData: renditions, per-device defaults,
// and the `{ type, src }` sources Video.js plays.
import stringUtils from "@/common/utils/stringUtils";
import config from "@/modules/config";
import { orientationType } from "@/modules/orientationManager";
import { MOBILE_DEFAULT_MODE } from "@/modules/experienceMediaUrls";
import ExperienceClusters from "@/modules/ExperienceClusters";
import type ExperienceMetadata from "@/modules/ExperienceMetadata";
import type { ExperiencePayload } from "@/types/experience";

/** A `{ type, src }` source for Video.js, or "" when there is no video. */
export type VideoSource = { type: string; src: string };

export default class ExperienceVideo extends ExperienceClusters {
  declare orientation: ExperiencePayload["orientation"];
  declare video: ExperiencePayload["video"];
  declare metadata: ExperienceMetadata;

  getDirectVideoSrc(mode: string | null, type?: string): VideoSource | "" {
    if (!this.video) {
      return "";
    }
    type = type || this.getVideoType();
    const filename = `${stringUtils.format(this.video.template, mode)}.${type}`;
    return {
      type: this.getVideoMime(type),
      src: this.metadata.getMediaUrl(filename),
    };
  }

  getApiVideoSrc(mode: string, type?: string): VideoSource | "" {
    if (!this.video) {
      return "";
    }
    type = type || this.getVideoType();
    const idvideo = this.video.idvideo;
    const mime = this.getVideoMime(type);
    const token = Date.now().toString(36);
    return {
      type: mime,
      src: this.metadata.getVideoUrl(idvideo, mode, mime, token),
    };
  }

  getVideoSrc(mode: string | null, type?: string): VideoSource | "" {
    return this.getDirectVideoSrc(mode, type);
  }

  getVideoModes(): string[] | null | undefined {
    if (!this.video) {
      return null;
    }
    return this.video.modes[this.getVideoType()];
  }

  getVideoMime(type: string): string {
    return `video/${type}`;
  }

  getVideoType(): string {
    return "mp4";
  }

  getVideoDefaults() {
    let ret;
    if (this.video == null || this.video.defaults == null) {
      return ret;
    }
    const defaults = this.video.defaults;
    if (Object.hasOwn(defaults, config.device)) {
      ret = defaults[config.device];
    }

    return ret;
  }

  getSpecifiedVideoMode(): string | undefined {
    const defaults = this.getVideoDefaults();
    let ret;
    if (defaults != null) {
      ret = defaults.mode;
    }
    return ret;
  }

  getDefaultVideoMode(): string | null {
    const mode = this.getSpecifiedVideoMode();
    if (mode != null) {
      return mode;
    }

    // No explicit per-device default configured. On mobile the player
    // renders at ~400px, so default to a small rendition (360p) instead of
    // the highest available; desktop keeps the high-def default.
    if (config.device === "mobile") {
      return this.getMobileDefaultVideoMode();
    }

    return this.getHighDefVideoMode();
  }

  getMobileDefaultVideoMode(): string | null {
    const modes = this.getVideoModes();
    if (modes == null) {
      return null;
    }

    for (let i = 0; i < modes.length; i++) {
      if (modes[i] === MOBILE_DEFAULT_MODE) {
        return modes[i];
      }
    }
    // No 360p rendition — fall back to a low tier.
    return this.getLowDefVideoMode();
  }

  getLowDefVideoMode(): string | null {
    const modes = this.getVideoModes();
    if (modes == null) {
      return null;
    }

    if (modes.length > 2) {
      return modes[1];
    } else {
      return modes[0];
    }
  }

  getHighDefVideoMode(): string | null {
    const modes = this.getVideoModes();
    if (modes == null) {
      return null;
    }

    if (this.orientation === orientationType.PORTRAIT) {
      if (modes.length > 2) {
        return modes[1];
      } else {
        return modes[0];
      }
    } else {
      return modes[modes.length - 1];
    }
  }

  getDefaultVideoSrc(): VideoSource | "" {
    if (!this.video) {
      return "";
    }
    return this.getVideoSrc(this.getDefaultVideoMode());
  }

  getLowDefVideoSrc(): VideoSource | "" {
    if (!this.video) {
      return "";
    }
    return this.getVideoSrc(this.getLowDefVideoMode());
  }

  getHighDefVideoSrc(): VideoSource | "" {
    if (!this.video) {
      return "";
    }
    return this.getVideoSrc(this.getHighDefVideoMode());
  }

  getAutoPlay(): boolean | undefined {
    const defaults = this.getVideoDefaults();
    let ret: boolean | undefined = false;
    if (defaults != null) {
      ret = defaults.autoPlay;
    }
    return ret;
  }
}
